/**
 * useEventDetailPage - Central hook for event detail page state and data
 *
 * Provides all the data, state, and callbacks needed for the event detail page.
 * This allows the page content to be composed using DetailPageLayout slots.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "@shared/utils/internationalization";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import moment from "moment-timezone";

import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import usePublicProductCategoryService from "@app-contexts/public/features/products/data/services/usePublicProductCategoryService";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { useCartSummaryAutoOpen } from "@app-contexts/public/features/basket/hooks/useCartSummaryAutoOpen";
import { useUserVouchersAndBasketCode } from "@app-contexts/public/features/discounts/hooks/useUserVoucherAndBasketCode/useUserVoucherAndBasketCode";

import { useCharity } from "@shared/modules/events/components/donations/hooks/useCharity";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { AnalyticsService } from "@shared/services/analytics";
import {
  markPageStart,
  markFirstContentPainted,
  markAllComponentsReady,
} from "@shared/utils/datadog/datadog-timings";
import useMediaQuery from "@shared/hooks/useMediaQuery/useMediaQuery";
import { useHashNavigation } from "@app-contexts/public/features/detail-pages/hooks/useHashNavigation";

import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { stringToSlug, isStringWithContent } from "@shared/utils/text/utils";
import {
  eventHasWaitlistItems,
  getAllowedRegions,
  isPastEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import breakpoints from "@shared/theme/props/breakpoints";
import usePublicBrandingConfigService from "@shared/modules/branding/data/services/usePublicBrandingConfigService";
import type { BrandingConfigSerializer } from "@shared/modules/branding/data/types";
import type { ProductCategoryTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import {
  WAFFLE_FLAG_NEW_PUBLIC_TEMPLATES,
  WAFFLE_FLAG_BRANDED_PAGES,
} from "@shared/constants/waffle-flags";
import type { PublicBaseEventSerializer } from "../data/types/events";

const { sm, lg } = breakpoints;

interface TabOption {
  id: number;
  title: string;
  showTab: boolean;
  hash: string | null;
  openModal?: () => void;
}

interface AttractionConfigItem {
  title: string;
  sort_order: number;
  ids: number[];
}

interface UseEventDetailPageOptions {
  initialEventData?: PublicBaseEventSerializer;
  cachedProductCategories?: ProductCategoryTypeSerializer[];
  initialBrandingConfig?: BrandingConfigSerializer | null;
}

export function useEventDetailPage({
  initialEventData,
  cachedProductCategories,
  initialBrandingConfig,
}: UseEventDetailPageOptions) {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();
  const { isReactNativeApp } = useNativeAppCheck();
  const { isFlagActive, SWITCHES } = useDjangoWaffle();

  // Responsive
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const [isTablet] = useMediaQuery(
    `(min-width: 0px) and (max-width: ${lg - 1}px)`
  );
  const scrollMargin = useDetailPageScrollMargin();

  // Modal controls
  const contactDetailsModal = useDisclosure();
  const productModal = useDisclosure();
  const ticketModal = useDisclosure();
  const shareModal = useDisclosure();

  // Basket & state
  const publicBasket = usePublicBasket();
  const { isBasketUpdating, totalItemQuantity } = publicBasket;
  const [viewMap, setViewMap] = useState(false);
  const [selectedTab, setSelectedTab] = useState(-1);
  // For non-password-protected events with SSR data, initialize to [] (all tickets
  // visible) so EventAuthorization can render children immediately without waiting
  // for the async loadAuthorizedToViewEvent call.
  const [ticketsAuthorizedByPassword, setTicketsAuthorizedByPassword] =
    useState<number[] | null>(() => {
      if (initialEventData && !initialEventData.is_password_protected) {
        return [];
      }
      return null;
    });

  // Reset on client-side navigation between events (same page component, different slug).
  // useState initializers only run on mount, so we need this effect to reset
  // ticketsAuthorizedByPassword for the new event.
  const eventSlug = router?.query?.eventSlug?.toString();
  const prevEventSlugRef = useRef(eventSlug);
  useEffect(() => {
    if (prevEventSlugRef.current !== eventSlug && eventSlug) {
      prevEventSlugRef.current = eventSlug;
      setTicketsAuthorizedByPassword(null);
    }
  }, [eventSlug]);

  const lastManualTabChangeRef = useRef<number | null>(null);

  // Event data fetching
  const eventParams = {
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
    source_channel: router?.query?.source_channel?.toString(),
  };

  const { useRetrieveWithMembershipTicketTypesOverride: useGetWithSlug } =
    usePublicEventService();
  const {
    data: eventData,
    isLoading,
    isError,
  } = useGetWithSlug(String(router?.query?.eventSlug), {
    config: { params: eventParams },
    options: {
      initialData: initialEventData,
      enabled: router.isReady && Boolean(router?.query?.eventSlug?.toString()),
    },
  });

  const isBrandingActive =
    isFlagActive(WAFFLE_FLAG_NEW_PUBLIC_TEMPLATES, eventData?.venue?.id) &&
    isFlagActive(WAFFLE_FLAG_BRANDED_PAGES, eventData?.venue?.id);

  // Venue branding config (public API - no venue auth needed)
  // Uses initialBrandingConfig from SSR to prevent flash of unbranded content
  const { useResolve } = usePublicBrandingConfigService();
  const { data: venueBrandingConfig } = useResolve({
    modelType: "event",
    instanceId: eventData?.id ?? 0,
    initialData: initialBrandingConfig,
    enabled: isBrandingActive,
  });

  // Payment config (e.g. Stripe publishable key) is volatile; fetch client-side and keep it out of event cache.
  // We prefetch on page load so ticket UI can use React Query cache later without waiting on modal open.
  const shouldFetchVenuePaymentConfig =
    Boolean(SWITCHES?.enable_event_page_cache) && Boolean(eventData?.venue?.id);
  const { usePublicVenuePaymentConfig } = usePublicVenueService();
  usePublicVenuePaymentConfig(eventData?.venue?.id, {
    enabled: shouldFetchVenuePaymentConfig,
  });

  // Product categories
  const { useList } = usePublicProductCategoryService();
  const { data: productCategories, isLoading: isLoadingCategories } = useList({
    params: { venue__slug: eventData?.venue?.slug },
    options: { initialData: cachedProductCategories ?? undefined },
  });

  // Charity & vouchers
  const { cause } = useCharity(eventData?.charities_of_choice?.[0], eventData);
  const canFetchVouchers =
    isLoggedIn && !!eventData && !eventData.attraction_event_config;
  const { data: voucherData } = useUserVouchersAndBasketCode(
    { inputEvent: eventData ?? {}, basketVoucherCode: null },
    { enabled: Boolean(canFetchVouchers) }
  );

  // Cart auto-open
  const { cartSummaryRef } = useCartSummaryAutoOpen({
    itemQty: totalItemQuantity,
    venueId: eventData?.venue?.id,
  });

  // Derived info objects
  const eventInfo = {
    eventId: eventData?.id,
    eventLocationName: eventData?.location?.name,
    eventAddress: `${eventData?.location?.street_name}, ${eventData?.location?.city}`,
    eventLatLng: eventData?.location?.position,
    eventVirtualType: eventData?.virtual_event_type,
    eventAccommodationUrl: eventData?.event_detail_accommodation_link || "",
    viewMap,
    handleViewMap: setViewMap,
  };

  const venueInfo = {
    venueId: eventData?.venue?.id,
    venueSlug: eventData?.venue?.slug,
    venueDescription: eventData?.venue?.description,
    venueName: eventData?.venue?.name,
    venuePrivate: eventData?.venue?.is_test,
  };

  // Computed flags
  const allowedRegions = getAllowedRegions(eventData?.allowed_regions ?? []);
  const displayAllowedRegionsText =
    allowedRegions && !eventData?.region_is_allowed;
  const hasTicketTypes = (eventData?.ticket_types?.length ?? 0) > 0;
  const hasChildEvents = (eventData?.child_events?.length ?? 0) > 0;
  const ticketsNotRequired = eventData?.no_ticket_types;
  const soldOutEvent = eventData && isSoldOutEvent(eventData);
  const hasWaitlistItems = eventData && eventHasWaitlistItems(eventData);
  const pastEvent = eventData && isPastEvent(eventData);
  const recurringEvent = eventData?.is_recurring_parent;
  const isAttractionPage = !!eventData?.attraction_event_config;
  const isLoadingPage =
    ((isLoading || isLoadingCategories) && !initialEventData) || isError;

  const displayTicketsNotAvailableText =
    ticketsNotRequired ||
    pastEvent ||
    (soldOutEvent && !hasWaitlistItems) ||
    (!recurringEvent && !hasTicketTypes) ||
    (recurringEvent && !hasChildEvents);

  // Tab configuration
  const getEventTabOptions = useCallback(() => {
    const tabArray: TabOption[] = [];
    if (eventData && !isAttractionPage) {
      tabArray.push({
        id: 0,
        title: t("Additional info", { ns: "common" }),
        showTab: Boolean(
          eventData?.description && isStringWithContent(eventData?.description)
        ),
        hash: "additional-info",
      });
      tabArray.push({
        id: tabArray.length + 1,
        title: t("Important Info"),
        showTab: (eventData?.restrictions?.length ?? 0) > 0,
        hash: "important-info",
      });
      tabArray.push({
        id: tabArray.length + 1,
        title: t("Add-ons"),
        showTab: eventData?.has_products !== false,
        hash: null,
        openModal: productModal.onOpen,
      });
      tabArray.push({
        id: tabArray.length + 1,
        title: t("Accommodations"),
        showTab: !!eventInfo?.eventAccommodationUrl,
        hash: "accommodations",
      });
    } else if (eventData && isAttractionPage) {
      if (
        isFlagActive(
          "use_sections_for_attraction_page_nav",
          eventData?.venue?.id
        )
      ) {
        const config = eventData?.attraction_event_config as
          | AttractionConfigItem[]
          | undefined;
        const sorted =
          config?.sort((a, b) => a.sort_order - b.sort_order) ?? [];
        tabArray.push(
          ...sorted.map((att, i) => ({
            id: i,
            title: att.title,
            showTab: !!att.ids.length,
            hash: stringToSlug(att.title),
          }))
        );
      } else {
        tabArray.push({
          id: 0,
          title: eventData.no_ticket_types ? t("Free Event") : t("Tickets"),
          showTab: true,
          hash: "tickets",
        });
      }
      if (eventData.charities_of_choice?.[0] && cause) {
        tabArray.push({
          id: tabArray.length + 1,
          title: t("Donations"),
          showTab: true,
          hash: "donations",
        });
      }
      tabArray.push({
        id: tabArray.length + 1,
        title: t("Additional info", { ns: "common" }),
        showTab: true,
        hash: "additional-info",
      });
      tabArray.push({
        id: tabArray.length + 1,
        title: t("Accommodations"),
        showTab: !!eventInfo?.eventAccommodationUrl,
        hash: "accommodations",
      });
    }
    return tabArray;
  }, [
    eventData,
    t,
    cause,
    productModal.onOpen,
    eventInfo?.eventAccommodationUrl,
    isFlagActive,
    isAttractionPage,
  ]);

  const eventTabOptions = useMemo(
    () => getEventTabOptions(),
    [getEventTabOptions]
  );

  // Modal subtitle
  const getModalSubtitle = useCallback(() => {
    if (recurringEvent && hasChildEvents) return "";
    if (eventData?.date_time_to_be_determined) return t("Date and Time TBD");
    if (!eventData?.starts_on || !eventData?.timezone) return "";
    return moment(eventData.starts_on)
      .tz(eventData.timezone)
      .format("dddd MMM D, YYYY | h:mm A z");
  }, [recurringEvent, hasChildEvents, eventData, t]);

  // Tab change handler
  const handleManualTabChange = useCallback((tabId: number) => {
    lastManualTabChangeRef.current = tabId;
    setSelectedTab(tabId);
  }, []);

  // Analytics effects
  useEffect(() => {
    AnalyticsService.eventLandingPageViewed(
      initialEventData ?? undefined,
      isBrandingActive
        ? {
            custom_params: {
              has_branding: Boolean(venueBrandingConfig?.has_branding_config),
              branding_color_mode: venueBrandingConfig?.branding_color_mode,
              branding_layout_preset:
                venueBrandingConfig?.branding_layout_preset,
            },
          }
        : undefined
    );
  }, [initialEventData, venueBrandingConfig, isBrandingActive]);

  useEffect(() => {
    if (initialEventData) {
      markPageStart("event_detail_page", {
        event_id: initialEventData.id,
        event_slug: initialEventData.slug,
        is_attraction: !!initialEventData.attraction_event_config,
        venue_id: initialEventData.venue?.id,
      });
    }
  }, [initialEventData]);

  useEffect(() => {
    if (eventData && !isLoading) {
      markFirstContentPainted("event_detail_page", {
        event_id: eventData.id,
        has_tickets: !!eventData.ticket_types?.length,
        has_products: !!eventData.has_products,
        is_sold_out: isSoldOutEvent(eventData),
      });
    }
  }, [eventData, isLoading]);

  useEffect(() => {
    if (eventData && !isLoading && !isLoadingCategories) {
      markAllComponentsReady("event_detail_page", {
        event_id: eventData.id,
        product_categories_count: productCategories?.length || 0,
        ticket_types_count: eventData.ticket_types?.length || 0,
      });
    }
  }, [eventData, isLoading, isLoadingCategories, productCategories]);

  // Navigation/redirect
  useEffect(() => {
    if (!router.isReady) return;
    if (isError)
      router.push({ pathname: "/404/" }, undefined, { shallow: true });
    else if (!router?.query?.eventSlug)
      router.push({ pathname: "/" }, undefined, { shallow: true });
  }, [isError, router.isReady, router?.query?.eventSlug, router]);

  // Hash/tab handling - handle URL hash changes (browser back/forward navigation)
  useHashNavigation({
    isReady: router.isReady && !isLoading && !!eventData,
    asPath: router?.asPath,
    tabOptions: eventTabOptions,
    setSelectedTab,
    hashRedirects: { description: "additional-info" },
    onRedirect: (hash) =>
      router.replace({ hash }, undefined, { shallow: true }),
    manualTabChangeRef: lastManualTabChangeRef,
  });

  return {
    // Data
    eventData,
    productCategories,
    voucherData,
    user,
    publicBasket,
    cause,

    // Loading states
    isLoading,
    isError,
    isLoadingCategories,
    isLoadingPage,

    // Flags
    isLoggedIn,
    isReactNativeApp,
    isAttractionPage,
    isTablet,
    isSmallerThanSm,
    displayAllowedRegionsText,
    displayTicketsNotAvailableText,
    pastEvent,
    soldOutEvent,
    ticketsNotRequired,
    hasWaitlistItems,
    allowedRegions,

    // Info objects
    eventInfo,
    venueInfo,

    // State
    viewMap,
    setViewMap,
    selectedTab,
    ticketsAuthorizedByPassword,
    setTicketsAuthorizedByPassword,
    scrollMargin,
    isBasketUpdating,
    cartSummaryRef,

    // Tabs
    eventTabOptions,
    handleManualTabChange,

    // Modal controls
    contactDetailsModal,
    productModal,
    ticketModal,
    shareModal,

    // Helpers
    getModalSubtitle,

    // Venue branding config
    venueBrandingConfig,
  };
}

export type EventDetailPageState = ReturnType<typeof useEventDetailPage>;
